<template>
    <div>
        <v-container fluid>
            <v-alert v-if="showSuccessNotification" type="success" dismissible @input="showSuccessNotification = false"
                class="mb-4 success-notification-banner" border="left" colored-border elevation="0">
                <div class="d-flex align-center">
                    <div class="flex-grow-1">
                        <div class="font-weight-bold black--text mb-1">
                            {{ $t("farm.youSharedAccessOfTheFarmWithTheFollowingUsersSuccessfully") }}
                        </div>
                        <div class="text-body-2 black--text">
                            {{ $t("farm.farmUsers") }}{{ $t("farm.s") }}: {{ successNotificationUsers }}
                        </div>
                    </div>
                </div>
            </v-alert>

            <div class="d-flex">
                <h2 class="font-weight-bolder">{{ $t("farm.farmUsers") }}</h2>
                <v-spacer></v-spacer>
                <v-btn dark class="mr-2 add-user-btn" color="primary" @click="handleAddNewUser">
                    {{ $t("farm.grantAccessToMoreUsers") }}
                </v-btn>
            </div>
            <!-- Data Table Card -->
            <v-card elevation="0" class="border-top-primary">
                <v-card-text>
                    <v-row style="margin-left: -30px; margin-bottom: 20px;">
                        <v-col cols="12">
                            <v-data-table :headers="tableHeaders" :items="users" :items-per-page="options.limit"
                                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }" item-key="id"
                                hide-default-footer :loading="loading" :options.sync="options"
                                :server-items-length="totalUsers" :loading-text="$t('farm.loadingData')">
                                <template v-slot:top>
                                    <div class="d-flex">
                                        <!-- Search Field -->
                                        <div class="ml-3 control" style="width: 280px;">
                                            <label for="text">&nbsp;</label>
                                            <v-text-field prepend-inner-icon="mdi-magnify" outlined :placeholder="$t('farm.search')"
                                                dense v-model="search" @input="searchQuery"
                                                class="shrink"></v-text-field>
                                        </div>

                                        <!-- User Role Dropdown -->
                                        <div class="ml-3 control">

                                            <label>{{ $t("farm.userRole") }}
                                                <v-tooltip top color="primary" max-width="220">
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-icon v-bind="attrs" v-on="on">
                                                            mdi-information-outline
                                                        </v-icon>
                                                    </template>
                                                    <span>{{ $t("farm.userRoleInfo") }}</span>
                                                </v-tooltip></label>
                                            <v-select v-model="userRole" :items="userRoleList" item-text="name"
                                                item-value="value" outlined dense :placeholder="$t('farm.all')"
                                                @change="handleRoleChange" style="width: 180px;" class="shrink"
                                                clearable></v-select>
                                        </div>

                                        <v-spacer></v-spacer>

                                        <div class="mt-4 d-flex">
                                            <!-- Pagination Info -->
                                            <div class="py-1 title">
                                                {{ currentPageStart }} {{ $t("farm.of") }} {{ totalUsers }}
                                            </div>

                                            <!-- Pagination Controls -->
                                            <v-btn class="mx-2" fab small outlined color="primary"
                                                :disabled="options.page <= 1" @click="pageChange(false)">
                                                <v-icon dark>mdi-chevron-left</v-icon>
                                            </v-btn>
                                            <v-btn class="mx-2" fab small outlined color="primary"
                                                :disabled="options.page >= totalPages" @click="pageChange(true)">
                                                <v-icon dark>mdi-chevron-right</v-icon>
                                            </v-btn>

                                            <!-- Customization Menu -->
                                            <v-menu v-model="menu" :close-on-content-click="false" :nudge-width="200"
                                                left :min-width="417">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn class="mx-2" fab small outlined color="primary"
                                                        v-bind="attrs" v-on="on">
                                                        <v-icon dark>mdi-cog-outline</v-icon>
                                                    </v-btn>
                                                </template>
                                                <v-card width="417">
                                                    <v-list>
                                                        <v-list-item>
                                                            <v-list-item-title>{{ $t("farm.customization")
                                                                }}</v-list-item-title>
                                                        </v-list-item>
                                                        <v-divider></v-divider>
                                                        <div class="pl-3 pt-2 font-weight-normal">
                                                            {{ $t("farm.recordsPerPage") }}
                                                        </div>
                                                        <v-list-item>
                                                            <v-slider v-model="itemsPerPageSlider"
                                                                :tick-labels="itemsPerPageOptions" :max="3" step="1"
                                                                ticks="always" tick-size="4"></v-slider>
                                                        </v-list-item>
                                                        <v-divider class="mt-n3"></v-divider>
                                                    </v-list>
                                                    <v-card-text>
                                                        <v-container fluid>
                                                            <v-row>
                                                                <v-col class="mt-n9" cols="6" v-for="header in headers"
                                                                    :key="header.value">
                                                                    <v-checkbox v-if="header.text"
                                                                        v-model="selectedHeaders" :label="header.text"
                                                                        color="primary" :value="header.text"
                                                                        hide-details></v-checkbox>
                                                                </v-col>
                                                            </v-row>
                                                        </v-container>
                                                    </v-card-text>
                                                    <v-layout justify-center>
                                                        <v-card-actions class="mb-5">
                                                            <v-spacer></v-spacer>
                                                            <v-btn outlined color="primary" @click="resetDefault"
                                                                width="190">
                                                                {{ $t("farm.restoreDefault") }}
                                                            </v-btn>
                                                            <v-btn color="primary" @click="resetTableStructure"
                                                                width="190">
                                                                {{ $t("farm.apply") }}
                                                            </v-btn>
                                                        </v-card-actions>
                                                    </v-layout>
                                                </v-card>
                                            </v-menu>

                                            <!-- Print Button -->
                                            <v-tooltip top color="black" max-width="350">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn class="mx-2" fab small outlined color="primary"
                                                        v-bind="attrs" v-on="on" @click="printPDF">
                                                        <v-icon dark>mdi-printer</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>{{ $t("farm.print") }}</span>
                                            </v-tooltip>
                                        </div>
                                    </div>
                                </template>

                                <!-- Individual column templates -->
                                <template v-slot:item.userNameAndId="{ item }">
                                    <div>
                                        <div class="font-weight-medium">{{ item.userName }}</div>
                                        <div class="text-caption text--secondary">{{ item.userId }}</div>
                                    </div>
                                </template>

                                <template v-slot:item.email="{ item }">
                                    <span class="text-truncate">{{ item.email || '-' }}</span>
                                </template>

                                <template v-slot:item.userRoleAndDate="{ item }">
                                    <div>
                                        <div class="font-weight-medium">{{ item.userRole }}</div>
                                        <div class="text-caption text--secondary">{{ item.registrationDate }}</div>
                                    </div>
                                </template>

                                <template v-slot:item.status="{ item }">
                                    <v-btn small rounded min-width="120" depressed :class="getStatusClass(item.status)"
                                        height="40">
                                        {{ getStatusText(item.status) }}
                                    </v-btn>
                                </template>

                                <template v-slot:item.access="{ item }">
                                    <a href="#" class="access-link" @click="removeUserAccess(item)">
                                        {{ $t("farm.removeAccess") }}
                                    </a>
                                </template>

                                <!-- <template v-slot:item.actions="{ item }">
                                    <div class="d-flex justify-center">
                                        <v-menu location="start">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon v-bind="attrs" v-on="on" small>
                                                    <v-icon>mdi-dots-vertical</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list>
                                                <v-list-item @click="viewUser(item)">
                                                    <v-list-item-title>View</v-list-item-title>
                                                </v-list-item>
                                                <v-list-item @click="editUser(item)">
                                                    <v-list-item-title>Edit</v-list-item-title>
                                                </v-list-item>
                                                <v-list-item @click="toggleUserStatus(item)">
                                                    <v-list-item-title
                                                        :style="item.status === 'active' ? 'color: red;' : 'color: green;'">
                                                        {{ item.status === 'active' ? 'Deactivate' : 'Activate' }}
                                                    </v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </div>
                                </template> -->

                                <template v-slot:footer></template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-container>

        <ConfirmationBox
        :dialog="showConfirmationBox"
        :message="confirmationMessage"
        :dialogTitle="confirmationDialogTitle"
        :confirmButtonText="$t('farm.confirm')"
        :confirmData="confirmationData"
        @close="showConfirmationBox = false"
        @confirm="handleConfirmationConfirm"
        />
        
        <AddNewUserToFarm v-model="showAddUserDialog" @users-selected="handleUsersSelected" :farm="farm" />
    </div>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import download from "@/mixins/DownloadMixin";
import html2pdf from "html2pdf.js";
import printJS from "print-js";
import ConfirmationBox from "./ConfirmationBox";
import AddNewUserToFarm from "./AddNewUserToFarm.vue";
import FarmService from "@/_services/FarmService";

export default {
    name: "FarmUser",
    components: {
        ConfirmationBox,
        AddNewUserToFarm,
    },
    props: {
        farm: {
            type: Object,
            default: () => ({}),
        },
    },
    async mounted() {
        // Initialize user role list with translations
        this.userRoleList = [
            { name: this.$t("farm.all"), value: "" },
            { name: this.$t("farm.farmer"), value: "farmer" },
            { name: this.$t("farm.technician"), value: "technician" },
        ];
        
        // Initialize headers with translations
        this.headers = [
            {
                text: this.$t("farm.userNameAndId"),
                align: "start",
                value: "userNameAndId",
                class: "black--text",
                sortable: true,
            },
            {
                text: this.$t("farm.email"),
                align: "start",
                value: "email",
                class: "black--text",
                sortable: true,
            },
            {
                text: this.$t("farm.userRoleAndRegDate"),
                align: "start",
                value: "userRoleAndDate",
                class: "black--text",
                sortable: true,
            },
            {
                text: this.$t("farm.status"),
                align: "start",
                value: "status",
                class: "black--text",
                sortable: true,
            },
            {
                text: this.$t("farm.access"),
                align: "start", 
                value: "access",
                class: "black--text",
                sortable: false,
            }
        ];
        
        // Initialize allSelectedHeaders with translations
        this.allSelectedHeaders = [
            this.$t("farm.userNameAndId"),
            this.$t("farm.email"),
            this.$t("farm.userRoleAndRegDate"),
            this.$t("farm.status"),
            this.$t("farm.access"),
        ];
        
        // Initialize confirm dialog texts
        this.confirmDeactivate.cancelBtnText = this.$t("farm.cancel");
        this.confirmationDialogTitle = this.$t("farm.grantFarmAccess");
        
        this.tableHeaders = this.headers;
        this.selectedHeaders = this.allSelectedHeaders;
        const currentLimitIndex = this.itemsPerPageOptions.indexOf(this.options.limit);
        this.itemsPerPageSlider = currentLimitIndex >= 0 ? currentLimitIndex : 0;
        this.loadFarmUsers();
    },
    computed: {
        totalPages() {
            return parseInt(Math.ceil(this.totalUsers / this.options.limit));
        },
        currentPageStart() {
            if (this.totalUsers === 0) {
                return '0';
            }
            const start = (this.options.page - 1) * this.options.limit + 1;
            const end = Math.min(this.options.page * this.options.limit, this.totalUsers);
            if (start === end) {
                return start.toString();
            }
            return `${start}-${end}`;
        },
    },
    watch: {
        options: {
            handler(newVal, oldVal) {
                if (oldVal && (newVal.page !== oldVal.page || newVal.limit !== oldVal.limit)) {
                    this.loadFarmUsers();
                }
            },
            deep: true,
        },
    },
    data() {
        return {
            debounceTimer: null,
            search: "",
            userRole: "",
            userRoleList: [],
            loading: false,
            options: {
                limit: 10,
                page: 1,
            },
            headers: [],
            allSelectedHeaders: [],
            menu: false,
            users: [],
            totalUsers: 0,
            itemsPerPageOptions: [5, 10, 25, 50],
            itemsPerPageSlider: 1,
            selectedHeaders: [],
            tableHeaders: [],   
            confirmDeactivateDialog: false,
            confirmDeactivate: {
                title: "",
                message: "",
                confirmBtnText: "",
                cancelBtnText: "Cancel",
                confirmData: {},
            },
            showAddUserDialog: false,
            showSuccessNotification: false,
            successNotificationUsers: "",
            showConfirmationBox: false,
            confirmationMessage: "",
            confirmationDialogTitle: "",
            confirmationData: {},
            selected: [],
            selectedUserAccess: [],
        };
    },
    methods: {
        removeUserAccess(item) {
            this.selectedUserAccess = [item];
            const userName = item.userName || this.$t("farm.thisUser");
            this.confirmationMessage = `${this.$t("farm.areYouSureYouWantToRemoveAccessFor")} ${userName}?`;
            this.confirmationDialogTitle = this.$t("farm.removeFarmAccess");
            this.confirmationData = {
                action: 'remove',
                farmIds: [this.farm.id],
                userIds: [item.userId],
                item: item
            };
            this.showConfirmationBox = true;
        },
        async loadFarmUsers() {
            this.loading = true;
            try {
                const params = {
                    page: this.options.page,
                    limit: this.options.limit,
                    availability : 'unavailable',
                    ...(this.search && { search: this.search }),
                    ...(this.userRole && { userType: this.userRole }),
                };
                
                const response = await FarmService.getUnavailableFarmUsers(this.farm.id, params);
                
                if (response && response.data) {
                    const apiData = response.data;
                    
                    if (apiData.users && Array.isArray(apiData.users)) {
                        // Map API response to component format
                        this.users = apiData.users.map(user => ({
                            id: user.id,
                            userId: user.id,
                            userName: user.name || '-',
                            email: user.email || '-',
                            userRole: user.userType || '-',
                            registrationDate: user.registrationDate 
                                ? new Date(user.registrationDate).toLocaleDateString() 
                                : '-',
                            status: user.status || 'active',
                        }));
                        
                        this.totalUsers = apiData.count || 0;
                    } else {
                        this.users = [];
                        this.totalUsers = 0;
                    }
                } else {
                    this.users = [];
                    this.totalUsers = 0;
                }
            } catch (error) {
                console.error('Error loading app users:', error);
                this.$notify({
                    title: this.$t("farm.error"),
                    message: error?.data?.message || this.$t("farm.failedToLoadUsersPleaseTryAgain"),
                    type: "error",
                });
                this.users = [];
                this.totalUsers = 0;
            } finally {
                this.loading = false;
            }
        },
        searchQuery() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.options.page = 1;
                this.loadFarmUsers();
            }, 500);
        },
        handleRoleChange() {
            this.options.page = 1;
            this.loadFarmUsers();
        },
        pageChange(next) {
            if (next) this.options.page += 1;
            else this.options.page -= 1;
            this.loadFarmUsers();
        },
        async resetDefault() {
            this.tableHeaders = this.headers;
            this.selectedHeaders = this.allSelectedHeaders;
            // Reset slider to default (index 0 = 5 items per page)
            this.itemsPerPageSlider = 0;
            this.options.limit = this.itemsPerPageOptions[this.itemsPerPageSlider];
            this.options.page = 1;
            this.menu = false;
            await this.loadFarmUsers();
        },
        async resetTableStructure() {
            this.tableHeaders = this.headers.filter((header) =>
                this.selectedHeaders.includes(header.text)
            );
            this.options.limit = this.itemsPerPageOptions[this.itemsPerPageSlider];
            this.options.page = 1;
            this.menu = false;
            await this.loadFarmUsers();
        },
        getStatusText(status) {
            switch (status) {
                case "active":
                    return this.$t("farm.active");
                case "inactive":
                    return this.$t("farm.inactive");
                default:
                    return status || this.$t("farm.na");
            }
        },
        getStatusClass(status) {
            switch (status) {
                case "active":
                    return "green-button";
                case "inactive":
                    return "red-button";
                default:
                    return "gray-button";
            }
        },
        handleAddNewUser() {
            this.showAddUserDialog = true;
        },
        handleUsersSelected(selectedUsers) {
            this.selected = selectedUsers;
            
            const userNames = selectedUsers.map(user => user.userName || user.name || this.$t("farm.unknownUser")).join(', ');
            
            this.confirmationMessage = `${this.$t("farm.areYouSureYouWantToGrantAccessToThisFarmForTheFollowingUsers")}\n\n${this.$t("farm.farmUsers")}${this.$t("farm.s")}: ${userNames}`;
            this.confirmationDialogTitle = this.$t("farm.grantFarmAccess");
            this.confirmationData = {
                action: 'grant',
                selected: selectedUsers
            };
            
            this.showConfirmationBox = true;
        },
        async handleConfirmationConfirm(confirmData) {
            this.showConfirmationBox = false;
            this.startLoading();
            
            try {
                // Handle remove access action
                if (confirmData?.action === 'remove') {
                    const { farmIds, userIds } = confirmData;
                    
                    if (!farmIds || !userIds) {
                        this.$notify({
                            title: this.$t("farm.error"),
                            message: this.$t("farm.farmIdAndUserIdAreRequired"),
                            type: "error",
                        });
                        return;
                    }
                    
                    const payload = {
                        farmIds: farmIds,
                        userIds: userIds
                    };
                    
                    await FarmService.removeUserAccess(payload);
                    
                    this.$notify({
                        title: this.$t("farm.farmAccessRemoved"),
                        message: this.$t("farm.successfullyRemovedUserAccessFromThisFarm"),
                        type: "success",
                    });
                    
                    await this.loadFarmUsers();
                    
                    this.selectedUserAccess = [];
                    return;
                }
                
                // Handle grant access action (existing logic)
                const selectedUsers = confirmData?.selected || this.selected;
                
                if (!selectedUsers || selectedUsers.length === 0) {
                    this.$notify({
                        title: this.$t("farm.error"),
                        message: this.$t("farm.noUsersSelectedPleaseSelectAtLeastOneUser"),
                        type: "error",
                    });
                    return;
                }
                
                const payload = {
                    userIds: selectedUsers.map(u => u.id || u.userId),
                    farmIds: [this.farm.id],
                };
                
                await FarmService.grantFarmAccess(payload);
                
                const userNames = selectedUsers.map(user => user.userName || user.name || this.$t("farm.unknownUser")).join(', ');
                this.successNotificationUsers = userNames;
                
                this.showSuccessNotification = true;
                
                this.$notify({
                    title: this.$t("farm.farmAccessGranted"),
                    message: `${this.$t("farm.successfullyGrantedAccessToThisFarmFor")} ${selectedUsers.length} ${this.$t("farm.user")}${selectedUsers.length > 1 ? this.$t("farm.s") : ""}`,
                    type: "success",
                });
                
                await this.loadFarmUsers();
                
                this.selected = [];
            } catch (error) {
                console.error('Error processing farm access:', error);
                let errorMessage = error?.data?.message;
                if (!errorMessage) {
                    if (confirmData?.action === 'remove') {
                        errorMessage = this.$t("farm.failedToRemoveFarmAccessPleaseTryAgain");
                    } else {
                        errorMessage = this.$t("farm.failedToGrantFarmAccessPleaseTryAgain");
                    }
                }
                this.$notify({
                    title: this.$t("farm.error"),
                    message: errorMessage,
                    type: "error",
                });
            } finally {
                this.stopLoading();
            }
        },
        viewUser(item) {
            // TODO: Implement view user functionality
            console.log("View user:", item);
        },
        editUser(item) {
            // TODO: Implement edit user functionality
            console.log("Edit user:", item);
        },
        toggleUserStatus(item) {
            // This method is not currently used but keeping for future use
            this.confirmDeactivate.title = item.status === "active" ? this.$t("farm.userDeactivated") : this.$t("farm.userActivated");
            this.confirmDeactivate.message =
                item.status === "active"
                    ? `${this.$t("farm.areYouSureYouWantToRemoveAccessFor")} ${item.userName}?`
                    : `${this.$t("farm.areYouSureYouWantToGrantAccessTo")} ${item.userName}?`;
            this.confirmDeactivate.confirmBtnText = item.status === "active" ? this.$t("farm.removeAccess") : this.$t("farm.grantAccess");
            this.confirmDeactivate.confirmData = { item };
            this.confirmDeactivateDialog = true;
        },
        async confirmDeactivateUser(confirmData) {
            this.confirmDeactivateDialog = false;
            const item = confirmData.item;
            const userIndex = this.users.findIndex((u) => u.id === item.id);
            if (userIndex !== -1) {
                this.users[userIndex].status = item.status === "active" ? "inactive" : "active";
                this.$notify({
                    title: item.status === "active" ? this.$t("farm.userDeactivated") : this.$t("farm.userActivated"),
                    type: "success",
                });
                await this.loadFarmUsers();
            }
        },
        async printPDF() {
            await this.$nextTick();
            const table = document.querySelector(".v-data-table__wrapper table");
            let removedHeader,
                removedCells = [];
            if (table) {
                const headerRow = table.querySelector("thead tr");
                if (headerRow && headerRow.children.length > 0) {
                    removedHeader = headerRow.removeChild(headerRow.lastElementChild);
                }
                const bodyRows = table.querySelectorAll("tbody tr");
                bodyRows.forEach((row) => {
                    if (row.children.length > 0) {
                        removedCells.push(row.removeChild(row.lastElementChild));
                    }
                });
            }
            const element = document.querySelector(".v-data-table__wrapper");
            let opt = {
                margin: 1,
                filename: "FarmUsers.pdf",
                image: { type: "jpeg", quality: 0.95 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
            };
            let worker = await html2pdf()
                .set(opt)
                .from(element)
                .toCanvas()
                .outputPdf("blob", "FarmUsers.pdf");
            let file = new File([worker], "FarmUsers.pdf", { type: "application/pdf" });
            var pdfUrl = await URL.createObjectURL(file);
            await printJS({ printable: `${pdfUrl}`, type: "pdf", showModal: true });
            if (table && removedHeader) {
                const headerRow = table.querySelector("thead tr");
                if (headerRow) {
                    headerRow.appendChild(removedHeader);
                }
                const bodyRows = table.querySelectorAll("tbody tr");
                bodyRows.forEach((row, idx) => {
                    if (removedCells[idx]) {
                        row.appendChild(removedCells[idx]);
                    }
                });
            }
            await this.$nextTick();
        },
    },
    mixins: [loadingMixin, download],
};
</script>

<style lang="scss" scoped>
.add-user-btn {
    color: white !important;
    text-transform: uppercase !important;
    font-weight: bold !important;
}

.success-notification-banner {
    background-color: #e8f5e9 !important;
    border: 2px solid #4caf50 !important;
    border-radius: 4px !important;
    padding: 24px 32px !important;
}

.success-notification-banner ::v-deep .v-alert__wrapper {
    padding: 0 !important;
}

.success-notification-banner ::v-deep .v-alert__content {
    width: 100% !important;
    padding: 0 !important;
}

.success-notification-banner ::v-deep .v-alert__dismissible {
    padding: 0 !important;
}

.green-button {
    background-color: #c1ffe7 !important;
    border: 2px solid #00bd73 !important;
    color: #489e16 !important;
}

.red-button {
    background-color: #fdecea !important;
    border: 2px solid #f03737 !important;
    color: #f03737 !important;
}

.gray-button {
    background-color: #e8e8e8 !important;
    border: 2px solid #000000 !important;
    color: #000000 !important;
}

::v-deep .v-data-table-header span {
    text-transform: uppercase !important;
    color: black;
}

.control {
    min-width: 120px;
    max-width: 220px;
    flex: 1 1 160px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
}

.access-link {
    color: #117443 !important;
    cursor: pointer !important;
}

@media print {
    .action-col {
        display: none !important;
    }
}
</style>
